import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import gsap from 'gsap';
import type { DiaryEntry } from '../types/diary.types';

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

type PageContent = {
  entryId: string;
  date: string;
  text: string;
  isStart: boolean;
};

type DiaryBookProps = {
  dark: boolean;
  entries: DiaryEntry[];
  isLoading: boolean;
  error: string | null;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onAdminClick: () => void;
};

export const DiaryBook = ({
  dark,
  entries,
  isLoading,
  error,
  isOpen,
  onOpen,
  onClose,
  onAdminClick,
}: DiaryBookProps): React.JSX.Element => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState<PageContent[]>([]);
  const [isFlipping, setIsFlipping] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [flipDirection, setFlipDirection] = useState<'next' | 'prev' | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);
  const bookContainerRef = useRef<HTMLDivElement>(null);
  const frontCoverRef = useRef<HTMLDivElement>(null);
  const pagesRef = useRef<HTMLDivElement>(null);
  const flipPageRef = useRef<HTMLDivElement>(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Sort entries by date
  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => new Date(b.entry_date).getTime() - new Date(a.entry_date).getTime()),
    [entries]
  );

  // Desktop: calculate pages by text-fitting
  useEffect(() => {
    if (!isOpen || sortedEntries.length === 0 || isMobile || !pageRef.current) return;

    const calculatePages = (): void => {
      const pageElement = pageRef.current;
      if (!pageElement) return;

      const pageHeight = pageElement.clientHeight;
      const pageWidth = pageElement.clientWidth;

      if (pageHeight === 0 || pageWidth === 0) {
        setTimeout(() => calculatePages(), 100);
        return;
      }

      const computedStyle = window.getComputedStyle(pageElement);
      const fontSize = computedStyle.fontSize;
      const fontWeight = computedStyle.fontWeight;
      const lineHeight = computedStyle.lineHeight;
      const fontFamily = computedStyle.fontFamily;
      const letterSpacing = computedStyle.letterSpacing;

      const headerFontSize = '1.6rem';
      const headerMarginBottom = '1rem';
      const headerPaddingBottom = '0.75rem';

      const measureDiv = document.createElement('div');
      measureDiv.style.position = 'absolute';
      measureDiv.style.top = '-9999px';
      measureDiv.style.left = '0';
      measureDiv.style.width = `${pageWidth}px`;
      measureDiv.style.height = `${pageHeight}px`;
      measureDiv.style.overflow = 'hidden';
      measureDiv.style.fontSize = fontSize;
      measureDiv.style.lineHeight = lineHeight;
      measureDiv.style.fontFamily = fontFamily;
      measureDiv.style.fontWeight = fontWeight;
      measureDiv.style.letterSpacing = letterSpacing;
      measureDiv.style.color = '#2c1810';
      document.body.appendChild(measureDiv);

      const newPages: PageContent[] = [];

      for (const entry of sortedEntries) {
        const words = entry.content.split(' ');
        let remainingWords = [...words];
        let isStart = true;

        while (remainingWords.length > 0) {
          const headerHtml = isStart
            ? `<div style="font-family: 'Dancing Script', cursive; font-weight: 600; font-size: ${headerFontSize}; text-align: center; margin-bottom: ${headerMarginBottom}; padding-bottom: ${headerPaddingBottom}; border-bottom: 1px solid rgba(120, 53, 15, 0.2);">${formatDate(entry.entry_date)}</div>`
            : '';

          let low = 1;
          let high = remainingWords.length;
          let bestFit = 0;

          while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const testText = remainingWords.slice(0, mid).join(' ');
            measureDiv.innerHTML = headerHtml + `<div>${testText}</div>`;

            if (measureDiv.scrollHeight <= measureDiv.clientHeight) {
              bestFit = mid;
              low = mid + 1;
            } else {
              high = mid - 1;
            }
          }

          if (bestFit === 0) bestFit = 1;

          newPages.push({
            entryId: entry.id,
            date: entry.entry_date,
            text: remainingWords.slice(0, bestFit).join(' '),
            isStart,
          });

          remainingWords = remainingWords.slice(bestFit);
          isStart = false;
        }
      }

      document.body.removeChild(measureDiv);
      setPages(newPages);

      if (currentPage >= newPages.length) {
        setCurrentPage(Math.max(0, newPages.length - 1));
      }
    };

    const waitForFontAndCalculate = async (): Promise<void> => {
      try {
        await document.fonts.ready;
        await new Promise(resolve => setTimeout(resolve, 200));
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            calculatePages();
          });
        });
      } catch {
        setTimeout(() => calculatePages(), 500);
      }
    };

    waitForFontAndCalculate();
  }, [sortedEntries, isOpen, isMobile]);

  // On mobile: one entry per page, on desktop: spreads (2 pages at a time)
  const totalPages = pages.length;
  const mobileTotalEntries = sortedEntries.length;
  const totalDisplayPages = isMobile ? mobileTotalEntries : Math.ceil(totalPages / 2);
  const canGoNext = isMobile
    ? currentPage < mobileTotalEntries - 1
    : currentPage < totalDisplayPages - 1;
  const canGoPrev = currentPage > 0;

  // Get current pages to display
  const getCurrentPages = useCallback(() => {
    if (isMobile) {
      return { left: pages[currentPage] || null, right: null };
    }
    const leftIndex = currentPage * 2;
    const rightIndex = leftIndex + 1;
    return {
      left: pages[leftIndex] || null,
      right: pages[rightIndex] || null,
    };
  }, [isMobile, currentPage, pages]);

  const goToNextPage = useCallback(() => {
    const maxPage = isMobile ? mobileTotalEntries - 1 : totalDisplayPages - 1;
    if (currentPage < maxPage && !isFlipping) {
      setIsFlipping(true);
      setFlipDirection('next');
    }
  }, [currentPage, mobileTotalEntries, totalDisplayPages, isFlipping, isMobile]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 0 && !isFlipping) {
      setIsFlipping(true);
      setFlipDirection('prev');
    }
  }, [currentPage, isFlipping]);

  // GSAP animation after flip page renders (desktop only)
  useEffect(() => {
    if (!isFlipping || !flipDirection) return;

    // Mobile: instant page change, no flip animation
    if (isMobile) {
      setCurrentPage((p) => (flipDirection === 'next' ? p + 1 : p - 1));
      setIsFlipping(false);
      setFlipDirection(null);
      return;
    }

    const animationFrame = requestAnimationFrame(() => {
      const flipPage = flipPageRef.current;
      if (!flipPage) {
        setIsFlipping(false);
        setFlipDirection(null);
        return;
      }

      if (flipDirection === 'next') {
        gsap.set(flipPage, { rotateY: 0 });
        gsap.to(flipPage, {
          rotateY: -180,
          duration: 0.6,
          ease: 'power2.inOut',
          onComplete: () => {
            setCurrentPage((p) => p + 1);
            setIsFlipping(false);
            setFlipDirection(null);
          },
        });
      } else if (flipDirection === 'prev') {
        gsap.set(flipPage, { rotateY: -180 });
        gsap.to(flipPage, {
          rotateY: 0,
          duration: 0.6,
          ease: 'power2.inOut',
          onComplete: () => {
            setCurrentPage((p) => p - 1);
            setIsFlipping(false);
            setFlipDirection(null);
          },
        });
      }
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [isFlipping, flipDirection, isMobile]);

  const handleOpen = useCallback(() => {
    if (isAnimating || !frontCoverRef.current || !pagesRef.current) return;

    setIsAnimating(true);

    const tl = gsap.timeline({
      onComplete: () => {
        onOpen();
        setIsAnimating(false);
      },
    });

    tl.to(frontCoverRef.current, {
      rotateY: -160,
      duration: 1.2,
      ease: 'power2.inOut',
    })
      .to(
        pagesRef.current,
        {
          rotateY: -15,
          duration: 0.8,
          ease: 'power2.out',
        },
        '-=0.6'
      )
      .to(
        bookContainerRef.current,
        {
          scale: 0.95,
          opacity: 0,
          duration: 0.3,
          ease: 'power2.in',
        },
        '-=0.3'
      );
  }, [onOpen, isAnimating]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') goToNextPage();
      if (e.key === 'ArrowLeft') goToPrevPage();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextPage, goToPrevPage, onClose, isOpen]);

  const currentPages = getCurrentPages();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500;600;700&display=swap');

        .diary-text {
          font-family: 'Caveat', cursive;
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.5;
          letter-spacing: 0.02em;
          color: #2c1810;
          text-align: left;
        }

        @media (min-width: 768px) {
          .diary-text {
            font-size: 1.75rem;
            line-height: 1.5;
          }
        }

        @media (min-width: 1200px) {
          .diary-text {
            font-size: 2.25rem;
            line-height: 1.45;
          }
        }

        .date-header {
          font-family: 'Dancing Script', cursive;
          font-weight: 600;
          font-size: 1.2rem;
          letter-spacing: 0.05em;
          color: #5c4033;
        }

        @media (min-width: 768px) {
          .date-header {
            font-size: 1.6rem;
          }
        }

        .book-title {
          font-family: 'Dancing Script', cursive;
          color: #d4af37;
          text-shadow:
            1px 1px 2px rgba(0,0,0,0.5),
            0 0 20px rgba(212, 175, 55, 0.3);
        }

        .parchment {
          background:
            radial-gradient(ellipse at 80% 20%, rgba(139, 90, 43, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 20% 80%, rgba(139, 90, 43, 0.06) 0%, transparent 50%),
            radial-gradient(ellipse at 60% 60%, rgba(101, 67, 33, 0.04) 0%, transparent 40%),
            linear-gradient(180deg,
              #f4e4c1 0%,
              #f0deb4 15%,
              #ecdcae 30%,
              #f2e2b8 50%,
              #ecdcae 70%,
              #e8d4a4 85%,
              #e4d0a0 100%
            );
        }

        .leather {
          background:
            radial-gradient(ellipse at 30% 20%, rgba(255,255,255,0.05) 0%, transparent 40%),
            radial-gradient(ellipse at 70% 80%, rgba(0,0,0,0.15) 0%, transparent 40%),
            linear-gradient(135deg,
              #1a0f08 0%,
              #2d1f15 20%,
              #3d2b1f 40%,
              #2d1f15 60%,
              #1a0f08 80%,
              #0d0805 100%
            );
        }
      `}</style>

      <div
        className={`fixed inset-0 flex items-center justify-center overflow-hidden transition-colors duration-700 ${
          dark
            ? 'bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950'
            : 'bg-gradient-to-b from-amber-950 via-stone-800 to-amber-950'
        }`}
      >
        {/* Ambient candlelight effect */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 30%, rgba(255, 200, 100, 0.08) 0%, transparent 50%)',
          }}
        />

        {/* ===== CLOSED BOOK ===== */}
        {!isOpen && (
          <div
            className="flex items-center justify-center px-4 w-full h-full"
            style={{ perspective: '2500px' }}
          >
            <div
              ref={bookContainerRef}
              className="relative"
              style={{
                width: isMobile ? 'min(85vw, 350px)' : 'min(70vw, 600px)',
                height: isMobile ? 'min(75vh, 500px)' : 'min(85vh, 800px)',
                transformStyle: 'preserve-3d',
              }}
            >
              {/* Pages under cover */}
              <div
                ref={pagesRef}
                className="absolute inset-0 parchment rounded-r-md"
                style={{
                  transformStyle: 'preserve-3d',
                  transformOrigin: 'left center',
                  boxShadow: 'inset 0 0 30px rgba(0,0,0,0.2)',
                }}
              >
                <div className="absolute inset-4 sm:inset-8 opacity-30">
                  {Array.from({ length: isMobile ? 10 : 15 }).map((_, i) => (
                    <div
                      key={i}
                      className="border-b border-amber-900/40"
                      style={{ marginTop: isMobile ? '1rem' : '1.5rem' }}
                    />
                  ))}
                </div>
                <div
                  className="absolute inset-4 sm:inset-8 flex items-center justify-center"
                  style={{ fontFamily: "'Dancing Script', cursive" }}
                >
                  <p className="text-amber-900/40 text-lg sm:text-xl md:text-3xl text-center">
                    Twoje wspomnienia...
                  </p>
                </div>
              </div>

              {/* Book spine */}
              <div
                className="absolute left-0 top-0 bottom-0 w-6 md:w-10 rounded-l-sm z-10"
                style={{
                  background: 'linear-gradient(90deg, #0d0805 0%, #1a0f08 30%, #2d1f15 50%, #1a0f08 70%, #0d0805 100%)',
                  boxShadow: '2px 0 10px rgba(0,0,0,0.5), inset -2px 0 5px rgba(0,0,0,0.3)',
                }}
              >
                {[12, 25, 75, 88].map((pos) => (
                  <div
                    key={pos}
                    className="absolute left-1 right-1 h-2 md:h-3 rounded-sm"
                    style={{
                      top: `${pos}%`,
                      background: 'linear-gradient(180deg, #3d2b1f 0%, #2d1f15 50%, #1a0f08 100%)',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    }}
                  />
                ))}
              </div>

              {/* Front cover */}
              <div
                ref={frontCoverRef}
                role="button"
                tabIndex={0}
                onClick={handleOpen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpen();
                  }
                }}
                className="absolute inset-0 cursor-pointer leather rounded-r-md hover:brightness-105 transition-[filter] duration-300"
                style={{
                  transformStyle: 'preserve-3d',
                  transformOrigin: 'left center',
                  boxShadow: `
                    8px 8px 25px rgba(0, 0, 0, 0.6),
                    inset -3px 0 8px rgba(255, 255, 255, 0.03),
                    inset 0 0 60px rgba(0, 0, 0, 0.4)
                  `,
                }}
              >
                <div
                  className="absolute inset-0 leather rounded-r-md"
                  style={{ backfaceVisibility: 'hidden' }}
                >
                  <div className="absolute left-4 sm:left-6 md:left-10 right-0 top-0 bottom-0 flex flex-col items-center justify-center p-4 sm:p-8 md:p-12 overflow-hidden">
                    <div className="text-amber-600/40 text-2xl sm:text-4xl md:text-5xl mb-2 sm:mb-6">❧</div>
                    <h1 className="book-title text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-wide sm:tracking-wider text-center">
                      Pamietniczek
                    </h1>
                    <p className="text-amber-700/50 text-sm sm:text-xl md:text-2xl mt-2 sm:mt-6 tracking-[0.1em]" style={{ fontFamily: "'Dancing Script', cursive" }}>
                      dla Blondaska
                    </p>
                    <div className="text-amber-600/40 text-2xl sm:text-4xl md:text-5xl mt-2 sm:mt-6 rotate-180">❧</div>
                    <p className="mt-4 sm:mt-8 text-amber-600/40 text-sm sm:text-xl md:text-2xl tracking-wide animate-pulse" style={{ fontFamily: "'Caveat', cursive" }}>
                      kliknij aby otworzyc
                    </p>
                  </div>

                  {/* Corner accents */}
                  <div className="absolute top-2 left-4 sm:left-6 md:left-10 w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 border-t-2 border-l-2 border-amber-700/40" />
                  <div className="absolute top-2 right-2 w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 border-t-2 border-r-2 border-amber-700/40" />
                  <div className="absolute bottom-2 left-4 sm:left-6 md:left-10 w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 border-b-2 border-l-2 border-amber-700/40" />
                  <div className="absolute bottom-2 right-2 w-4 sm:w-6 md:w-8 h-4 sm:h-6 md:h-8 border-b-2 border-r-2 border-amber-700/40" />
                </div>

                <div
                  className="absolute inset-0 parchment rounded-l-md"
                  style={{
                    backfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                  }}
                >
                  <div
                    className="absolute inset-4 rounded"
                    style={{
                      background: 'linear-gradient(135deg, rgba(139, 90, 43, 0.1) 0%, transparent 50%)',
                      border: '2px solid rgba(139, 90, 43, 0.2)',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== OPEN BOOK ===== */}
        {isOpen && (
          <>
            {/* Close button - top right corner */}
            <button
              onClick={onClose}
              className="fixed top-3 right-3 z-50 text-amber-100/50 hover:text-amber-100/80 transition-colors text-2xl sm:text-3xl p-2"
            >
              ✕
            </button>

            {/* Navigation - bottom of screen */}
            <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center items-center gap-2 sm:gap-4 px-2 sm:px-4 pb-2 sm:pb-4 bg-gradient-to-t from-black/30 to-transparent pt-8">
              <button
                onClick={goToPrevPage}
                disabled={!canGoPrev || isFlipping}
                className={`px-3 sm:px-5 py-2 sm:py-3 rounded-lg text-sm sm:text-lg transition-all ${
                  !canGoPrev || isFlipping
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-amber-900/50 active:scale-95'
                } bg-stone-900/80 text-amber-100/80 backdrop-blur border border-amber-900/30`}
                style={{ fontFamily: "'Caveat', cursive" }}
              >
                ←
              </button>

              <span className="px-2 sm:px-4 py-2 text-amber-200/60 text-sm sm:text-lg min-w-[60px] text-center" style={{ fontFamily: "'Caveat', cursive" }}>
                {currentPage + 1} / {totalDisplayPages}
              </span>

              <button
                onClick={goToNextPage}
                disabled={!canGoNext || isFlipping}
                className={`px-3 sm:px-5 py-2 sm:py-3 rounded-lg text-sm sm:text-lg transition-all ${
                  !canGoNext || isFlipping
                    ? 'opacity-30 cursor-not-allowed'
                    : 'hover:bg-amber-900/50 active:scale-95'
                } bg-stone-900/80 text-amber-100/80 backdrop-blur border border-amber-900/30`}
                style={{ fontFamily: "'Caveat', cursive" }}
              >
                →
              </button>
            </div>

            {/* The open book */}
            <div
              className="relative w-full h-full md:w-[95vw] md:h-[85vh] md:max-w-[2000px] p-2 sm:p-4"
              style={{ perspective: '2500px' }}
            >
              {/* Book binding/cover frame */}
              <div
                className="w-full h-full leather rounded-lg"
                style={{
                  padding: isMobile ? '6px' : '12px',
                  boxShadow: `
                    0 20px 60px rgba(0, 0, 0, 0.7),
                    inset 0 0 40px rgba(0, 0, 0, 0.5)
                  `,
                }}
              >
                {/* Inner pages container */}
                <div
                  className="relative w-full h-full rounded overflow-hidden"
                  style={{
                    boxShadow: 'inset 0 0 20px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {isLoading ? (
                    <div className="w-full h-full flex items-center justify-center parchment">
                      <div className="text-center text-amber-900/60">
                        <div className="text-4xl mb-4">📜</div>
                        <p className="text-xl" style={{ fontFamily: "'Caveat', cursive" }}>Ladowanie...</p>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="w-full h-full flex items-center justify-center parchment">
                      <div className="text-center text-red-900/70 p-4">
                        <p className="text-lg" style={{ fontFamily: "'Caveat', cursive" }}>{error}</p>
                      </div>
                    </div>
                  ) : entries.length === 0 ? (
                    <div className="w-full h-full flex items-center justify-center parchment">
                      <div className="text-center text-amber-900/50 p-4">
                        <p className="text-xl md:text-2xl" style={{ fontFamily: "'Caveat', cursive" }}>Puste strony czekaja na historie...</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="parchment relative w-full h-full"
                      style={{ padding: isMobile ? '1rem' : '2rem 3rem' }}
                    >
                      {/* Age stains for atmosphere */}
                      <div
                        className="absolute top-0 right-1/3 w-20 md:w-40 h-20 md:h-40 rounded-full opacity-15 pointer-events-none"
                        style={{ background: 'radial-gradient(circle, rgba(139, 90, 43, 0.3) 0%, transparent 70%)' }}
                      />
                      <div
                        className="absolute bottom-20 left-10 md:left-20 w-16 md:w-32 h-16 md:h-32 rounded-full opacity-10 pointer-events-none"
                        style={{ background: 'radial-gradient(circle, rgba(101, 67, 33, 0.4) 0%, transparent 70%)' }}
                      />

                      {/* Navigation hints on pages */}
                      {canGoPrev && !isFlipping && (
                        <button
                          onClick={goToPrevPage}
                          className="absolute left-1 top-1/2 -translate-y-1/2 text-amber-800/30 hover:text-amber-800/50 text-4xl md:text-6xl transition-colors z-10"
                        >
                          ‹
                        </button>
                      )}
                      {canGoNext && !isFlipping && (
                        <button
                          onClick={goToNextPage}
                          className="absolute right-1 top-1/2 -translate-y-1/2 text-amber-800/30 hover:text-amber-800/50 text-4xl md:text-6xl transition-colors z-10"
                        >
                          ›
                        </button>
                      )}

                      {/* Center spine shadow - only on desktop */}
                      {!isMobile && (
                        <div
                          className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-8 pointer-events-none z-10"
                          style={{
                            background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0.12) 50%, rgba(0,0,0,0.08) 60%, transparent 100%)',
                          }}
                        />
                      )}

                      {/* Page content */}
                      {isMobile ? (
                        // Mobile: scrollable single entry
                        <div
                          className="diary-text w-full h-full overflow-y-auto relative px-4 pb-16"
                        >
                          {sortedEntries[currentPage] && (
                            <>
                              <div className="date-header text-center mb-3 pb-2 border-b border-amber-900/20">
                                {formatDate(sortedEntries[currentPage].entry_date)}
                              </div>
                              <div>{sortedEntries[currentPage].content}</div>
                            </>
                          )}
                        </div>
                      ) : (
                        // Desktop: two pages side by side
                        <div className="flex w-full h-full gap-12" style={{ perspective: '2000px' }}>
                          {/* Left page */}
                          <div
                            ref={pageRef}
                            className="diary-text flex-1 h-full overflow-hidden relative"
                          >
                            {currentPages.left && (
                              <>
                                {currentPages.left.isStart && (
                                  <div className="date-header text-center mb-4 pb-3 border-b border-amber-900/20">
                                    {formatDate(currentPages.left.date)}
                                  </div>
                                )}
                                <div>{currentPages.left.text}</div>
                              </>
                            )}
                          </div>

                          {/* Right page */}
                          <div className="diary-text flex-1 h-full overflow-hidden relative">
                            {currentPages.right && (
                              <>
                                {currentPages.right.isStart && (
                                  <div className="date-header text-center mb-4 pb-3 border-b border-amber-900/20">
                                    {formatDate(currentPages.right.date)}
                                  </div>
                                )}
                                <div>{currentPages.right.text}</div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Flip page overlay */}
                      {isFlipping && flipDirection && (
                        <div
                          ref={flipPageRef}
                          className="absolute parchment diary-text overflow-hidden"
                          style={{
                            width: isMobile ? '100%' : 'calc(50% - 30px)',
                            height: '100%',
                            top: 0,
                            left: isMobile ? 0 : (flipDirection === 'next' ? 'calc(50% + 6px)' : '0'),
                            transformStyle: 'preserve-3d',
                            transformOrigin: isMobile
                              ? (flipDirection === 'next' ? 'left center' : 'right center')
                              : (flipDirection === 'next' ? 'left center' : 'right center'),
                            zIndex: 20,
                            boxShadow: '0 0 30px rgba(0,0,0,0.3)',
                            padding: isMobile ? '1rem' : '2rem',
                          }}
                        >
                          {/* Front of flipping page */}
                          <div
                            className="absolute inset-0 parchment p-4"
                            style={{ backfaceVisibility: 'hidden' }}
                          >
                            {flipDirection === 'next' && (
                              isMobile ? (
                                pages[currentPage] && (
                                  <>
                                    {pages[currentPage].isStart && (
                                      <div className="date-header text-center mb-3 pb-2 border-b border-amber-900/20">
                                        {formatDate(pages[currentPage].date)}
                                      </div>
                                    )}
                                    <div>{pages[currentPage].text}</div>
                                  </>
                                )
                              ) : (
                                currentPages.right && (
                                  <>
                                    {currentPages.right.isStart && (
                                      <div className="date-header text-center mb-4 pb-3 border-b border-amber-900/20">
                                        {formatDate(currentPages.right.date)}
                                      </div>
                                    )}
                                    <div>{currentPages.right.text}</div>
                                  </>
                                )
                              )
                            )}
                          </div>

                          {/* Back of flipping page */}
                          <div
                            className="absolute inset-0 parchment p-4"
                            style={{
                              backfaceVisibility: 'hidden',
                              transform: 'rotateY(180deg)',
                            }}
                          >
                            {flipDirection === 'next' && (
                              isMobile ? (
                                pages[currentPage + 1] && (
                                  <>
                                    {pages[currentPage + 1].isStart && (
                                      <div className="date-header text-center mb-3 pb-2 border-b border-amber-900/20">
                                        {formatDate(pages[currentPage + 1].date)}
                                      </div>
                                    )}
                                    <div>{pages[currentPage + 1].text}</div>
                                  </>
                                )
                              ) : (
                                pages[(currentPage + 1) * 2] && (
                                  <>
                                    {pages[(currentPage + 1) * 2].isStart && (
                                      <div className="date-header text-center mb-4 pb-3 border-b border-amber-900/20">
                                        {formatDate(pages[(currentPage + 1) * 2].date)}
                                      </div>
                                    )}
                                    <div>{pages[(currentPage + 1) * 2].text}</div>
                                  </>
                                )
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Admin link */}
        <button
          onClick={onAdminClick}
          className="fixed bottom-2 right-2 sm:bottom-4 sm:right-4 text-xs sm:text-base text-amber-900/30 hover:text-amber-800/50 transition-colors z-50"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          admin
        </button>
      </div>
    </>
  );
};
